import { Select, Text } from "@mantine/core";
import { DataTableColumn } from "mantine-datatable";
import { useCallback, useMemo } from "react";

import ActionIcon from "../../../components/ActionIcon/ActionIcon.tsx";
import { DatabaseMetadataPopover } from "../../../components/DatabaseMetadataPopover/DatabaseMetadataPopover.tsx";
import SvgEdit from "../../../components/Icons/Edit.tsx";
import { TableData, TableRow } from "../../../components/Table/types.ts";
import { BABABOS_TENANT_ID } from "../../../constants.ts";
import {
  TenantStatus,
  UpdateTenantStatusInput,
} from "../../../graphql/generated";
import { Tenant, tenantStatuses } from "../../../types/tenant.ts";
import { toLocalDateTime } from "../../../utils/date.ts";
import { toHumanReadable } from "../../../utils/string.ts";
import { useStyles } from "../TenantManagement.styles.ts";

interface Props {
  data: Tenant[] | null | undefined;
  onEdit: (id: number) => void;
  onInlineEdit: (id: number, value: UpdateTenantStatusInput) => void;
}

export function useTenantsTableData({
  data,
  onEdit,
  onInlineEdit,
}: Props): TableData {
  const { classes } = useStyles();

  const columns: DataTableColumn<TableRow>[] = [
    {
      accessor: "name",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{ tableName: "Tenant", fieldName: "name" }}
        >
          Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "status",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{ tableName: "Tenant", fieldName: "status" }}
        >
          Status
        </DatabaseMetadataPopover>
      ),
      sortable: true,
      width: 120,
    },
    {
      accessor: "picName",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Tenant",
            fieldName: "picName",
          }}
        >
          PIC Name
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "address",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Tenant",
            fieldName: "address",
          }}
        >
          Address
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "email",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Tenant",
            fieldName: "email",
          }}
        >
          Email
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "phone",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Tenant",
            fieldName: "phone",
          }}
        >
          Phone Number
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    {
      accessor: "createdAt",
      title: (
        <DatabaseMetadataPopover
          databaseMetadata={{
            tableName: "Tenant",
            fieldName: "createdAt",
          }}
        >
          Created Date
        </DatabaseMetadataPopover>
      ),
      sortable: true,
    },
    { accessor: "actions" },
  ];

  const handleOnInlineEdit = useCallback(
    (id: number, value: UpdateTenantStatusInput) => {
      onInlineEdit(id, value);
    },
    [onInlineEdit]
  );

  const rows = useMemo(() => {
    return data?.map((tenant) => {
      return {
        name: tenant.name,
        status:
          tenant.id !== BABABOS_TENANT_ID ? (
            <Select
              key={tenant.id}
              data={tenantStatuses}
              value={tenant.status}
              placeholder="Select a status"
              size="s"
              onChange={(value: TenantStatus) =>
                handleOnInlineEdit(tenant.id, { status: value })
              }
            />
          ) : (
            toHumanReadable(tenant.status)
          ),
        picName: tenant.picName,
        address: tenant.address,
        email: tenant.email && (
          <Text className={classes.emailWrapper}>{tenant.email}</Text>
        ),
        phone: tenant.phone,
        createdAt: toLocalDateTime(tenant.createdAt),
        actions: (
          <ActionIcon onClick={() => onEdit(tenant.id)}>
            <SvgEdit />
          </ActionIcon>
        ),
      };
    });
  }, [data, classes.emailWrapper, handleOnInlineEdit, onEdit]);
  return [columns, rows];
}
